import React, { lazy, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getCustomeBussinessRules } from "../../functions/customizeOrganization";
import { Elements, RecurlyProvider } from "@recurly/react-recurly";
import {
  wlsFreeSandboxRoles,
  wlsNoMeetService,
  wlsPremiumRollName,
} from "../../../customize/defaultValues";
import AiSocialPage from "../../../pages/AiSocial/AiSocialPage.tsx";
import { getOrgName } from "../../functions/url.js";
import PageMaintenance from "../../../pages/page-maintenance/PageMaintenance.js";

const StartMeeting = lazy(
  () => import("../../../pages/startMeeting/startMeeting"),
);

// Componente para redirigir a demo.mypxch.com o renderizar StartMeeting
const ExternalMeetingRedirect = () => {
  const { room, meetingNumber } = useParams();
  const exp = [];
  const expNum = [119161014, 111906516];

  const shouldRedirect =
    exp.includes(room) ||
    meetingNumber == 119161014 ||
    meetingNumber == 111906516;
  console.log(meetingNumber, shouldRedirect);
  useEffect(() => {
    if (shouldRedirect) {
      const baseUrl = "https://demo.mypxch.com";
      let redirectUrl;
      if (meetingNumber) {
        redirectUrl = `${baseUrl}/${room}/${meetingNumber}`;
      } else {
        redirectUrl = `${baseUrl}/${room}`;
      }
      window.location.href = redirectUrl;
    }
  }, [room, meetingNumber, shouldRedirect]);

  if (!shouldRedirect) {
    return <StartMeeting />;
  }

  return null;
};

const ExternalPersonalLinkRedirect = () => {
  const { room } = useParams();
  const exp = [];
  const shouldRedirect = exp.includes(room);

  useEffect(() => {
    if (shouldRedirect) {
      const redirectUrl = "https://demo.mypxch.com/stevepxch/111906516";
      window.location.href = redirectUrl;
    }
  }, [room, shouldRedirect]);

  if (!shouldRedirect) {
    return <StartMeeting />;
  }

  return null;
};

const MeetingsList = lazy(() => import("../../../pages/meetings/meetings"));
const MeetNewPage = lazy(() => import("../../../pages/meet-new-2/index.ts"));
const MeetingReportsPage = lazy(
  () => import("../../../pages/meeting-reports/MeetingReportsPage.tsx"),
);
const PaymentMain = lazy(() => import("../../../pages/payment/paymentMain"));

const DashboardMain = lazy(() => import("../../../pages/dasboard/dashboard"));
const MyRewards = lazy(() => import("../../../pages/my-rewards/my-rewards.js"));
const MyPayments = lazy(
  () => import("../../../pages/my-payments/my-payments.js"),
);
const UserManagment = lazy(
  () => import("../../../pages/user-managment/user-managment.js"),
);
const RecordingsList = lazy(
  () => import("../../../pages/recordings/RecordingsPage.tsx"),
);
const Profile = lazy(() => import("../../../pages/profile/profile"));
const Subscription = lazy(
  () => import("../../../pages/subscription/subscription"),
);
const InvoicesPage = lazy(
  () => import("../../../pages/invoices/InvoicesPage.tsx"),
);

const SettingsPage = lazy(() => import("../../../pages/settings/index.ts"));
const MyCohostsPage = lazy(
  () => import("../../../pages/cohosts/MyCohostsPage"),
);

const LinksPage = lazy(
  () => import("../../../pages/myFavoritesMedia/LinksPage/LinksPage.tsx"),
);
const AffiliatePage = lazy(
  () =>
    import("../../../pages/myFavoritesMedia/AffiliatePage/AffiliatePage.tsx"),
);
const PresentationsPage = lazy(
  () =>
    import("../../../pages/myFavoritesMedia/PresentationsPage/PresentationsPage.tsx"),
);
const TutorialsPage = lazy(
  () =>
    import("../../../pages/myFavoritesMedia/TutorialsPage/TutorialsPage.tsx"),
);

const AISocialInvitation = lazy(
  () => import("../../../pages/AiSocial/AiSocial.tsx"),
);

const ShareEnterprise = lazy(
  () => import("../../../pages/share-enterprise/ShareEnterprise.js"),
);

const BuyLicencesPage = lazy(
  () => import("../../../pages/page-buy-licences/BuyLicencesPage.tsx"),
);

// Family Plan (vista del padre). Convive con el flujo enterprise de
// `user-managment`, que no se modifica.
const FamilyOverviewPage = lazy(
  () => import("../../../pages/family-plan/FamilyOverviewPage.tsx"),
);
const FamilyMembersPage = lazy(
  () => import("../../../pages/family-plan/FamilyMembersPage.tsx"),
);
const FamilyTreePage = lazy(
  () => import("../../../pages/family-plan/FamilyTreePage.tsx"),
);
// My Family (My Account): resumen para padres, vista informativa para hijos y
// upgrade a Family para planes de pago. Free se redirige a /account.
const MyFamilyPage = lazy(
  () => import("../../../pages/family-plan/MyFamilyPage.tsx"),
);

const FamilyCheckoutPage = lazy(
  () => import("../../../pages/family-plan/FamilyCheckoutPage.tsx"),
);

const ReviewCartPage = lazy(
  () =>
    import("../../../pages/page-buy-licences/components/ReviewCartPage.tsx"),
);

const MyAvatarPage = lazy(
  () => import("../../../pages/my-avatar/MyAvatarPage.tsx"),
);

const ConfigureWaitingRoomPage = lazy(
  () => import("../../../pages/ai-waiting-room/ConfigureWaitingRoomPage.tsx"),
);

const AIToolsPage = lazy(
  () => import("../../../pages/ai-tools/AIToolsPage.tsx"),
);

const AIVideosPage = lazy(
  () => import("../../../pages/ai-tools/AIVideosPage.tsx"),
);

const BuyCreditsPage = lazy(
  () => import("../../../pages/ai-tools/BuyCreditsPage.tsx"),
);

const MyCreditsPage = lazy(
  () => import("../../../pages/my-credits/MyCreditsPage.tsx"),
);

const ThankYouPage = lazy(
  () => import("../../../pages/my-credits/ThankYouPage.section.tsx"),
);

const CheckoutEmbedPage = lazy(
  () => import("../../../pages/my-credits/CheckoutEmbedPage.tsx"),
);

const CommunicationCentralPage = lazy(
  () => import("../../../pages/communication-central/CommunicationCentralPage.tsx"),
);

const MySmartLinksPage = lazy(
  () => import("../../../pages/my-smart-link/MySmartLinksPage.tsx"),
);

const CreateSmartLinkPage = lazy(
  () => import("../../../pages/my-smart-link/CreateSmartLinkPage.tsx"),
);

const CohostRequestsPage = lazy(
  () => import("../../../pages/my-smart-link/components/requests/index.ts"),
);

const NotificationsPage = lazy(
  () => import("../../../pages/notifications/index.ts"),
);

const AmbassadorFlowPage = lazy(
  () => import("../../../pages/ambassador-flow/index.ts"),
);

const SmartLinksAccessGuard = lazy(
  () =>
    import(
      "../../../pages/my-smart-link/components/SmartLinksAccessGuard.component.tsx"
    ),
);

const CrmPage = lazy(() => import("../../../pages/crm/CrmPage.tsx"));

const customeRules = getCustomeBussinessRules();

export const routes = [
  {
    path: "ambassador-flow",
    active: true,
    Component: <AmbassadorFlowPage />,
  },
  {
    path: "payment-test",
    active: true,
    Component: <PaymentMain />,
  },

  {
    path: "/:room/:meetingNumber",
    active: true,
    Component: <ExternalMeetingRedirect />,
  },
  {
    path: "/sp/:room/:meetingNumber",
    active: true,
    Component: <StartMeeting />,
  },
  {
    path: "/my/:room",
    active: true,
    Component: <StartMeeting />,
  },
  {
    path: "/r/:room/:meetingNumber",
    active: true,
    Component: <StartMeeting />,
  },
];

export const dashboardRoutes = [
  {
    path: "dashboard",
    active: true,
    Component: <DashboardMain />,
    redirectOnHold: true,
    notRoles: wlsNoMeetService,
    redirectUrl: "/my-rewards",
  },
  // {
  //   path: "guest-dashboard",
  //   active: true,
  //   Component: <DashboardMain />,
  //   redirectOnHold: true,
  // },
  {
    path: "my-rewards",
    active: true,
    Component: <MyRewards />,
    redirectOnHold: true,
  },
  {
    path: "my-payments",
    active: true,
    Component: <MyPayments />,
    redirectOnHold: true,
  },
  {
    path: "user-managment",
    active: true,
    Component: <UserManagment />,
    redirectOnHold: true,
  },
  {
    path: "recordings",
    active: true,
    Component: <RecordingsList />,
    redirectOnHold: true,
    notRoles: wlsNoMeetService,
  },
  {
    path: "profile",
    active: true,
    Component: <Profile />,
    redirectOnHold: false,
  },
  {
    path: "account",
    active: true,
    Component: (
      <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
        <Elements>
          <Subscription />
        </Elements>
      </RecurlyProvider>
    ),
    redirectOnHold: false,
  },

  {
    path: "invoices",
    active: true,
    Component: <InvoicesPage />,
    redirectOnHold: false,
    notRoles: [...wlsNoMeetService, ...wlsFreeSandboxRoles],
  },
  {
    path: "settings",
    active: true,
    redirectOnHold: true,
    Component: <SettingsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "cohosts",
    active: true,
    redirectOnHold: true,
    grants: customeRules?.cohost?.type !== "none",
    Component: <MyCohostsPage />,
    roles: wlsPremiumRollName,
  },
  {
    path: "all-meetings",
    active: true,
    redirectOnHold: true,
    Component: <MeetingsList />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "meet",
    active: true,
    redirectOnHold: true,
    Component: <MeetNewPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "meeting-reports",
    active: true,
    redirectOnHold: true,
    Component: <MeetingReportsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "my-links",
    active: true,
    redirectOnHold: true,
    Component: <LinksPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "my-affiliates",
    active: true,
    redirectOnHold: true,
    Component: <AffiliatePage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "my-presentations",
    active: true,
    redirectOnHold: true,
    Component: <PresentationsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "tutorials",
    active: true,
    redirectOnHold: true,
    Component: <TutorialsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "generate-ai-social",
    active: true,
    redirectOnHold: true,
    Component: <AISocialInvitation />,
    //getOrgName() === "develop" ? <AISocialInvitation /> : <PageMaintenance />,
  },

  {
    path: "ai-invites",
    active: true,
    redirectOnHold: true,
    Component: <AiSocialPage />,
    //getOrgName() === "develop" ? <AiSocialPage /> : <PageMaintenance />,
  },
  {
    path: "share-enterprise",
    active: true,
    redirectOnHold: true,
    Component: <ShareEnterprise />,
  },
  {
    path: "buy-licenses",
    active: true,
    redirectOnHold: true,
    Component: <BuyLicencesPage />,
  },
  {
    path: "family-plan",
    active: true,
    redirectOnHold: true,
    Component: <FamilyOverviewPage />,
  },
  {
    path: "my-family",
    active: true,
    redirectOnHold: true,
    // El upgrade a Family puede pedir tarjeta (Recurly.js), igual que /account.
    Component: (
      <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
        <Elements>
          <MyFamilyPage />
        </Elements>
      </RecurlyProvider>
    ),
  },
  {
    /* Pasos 2 y 3 del alta al Family Plan. Es pagina y no modal a proposito:
       aqui se ejecuta un cargo real y un modal se cierra con un click. */
    path: "family-checkout",
    active: true,
    redirectOnHold: true,
    Component: (
      <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
        <Elements>
          <FamilyCheckoutPage />
        </Elements>
      </RecurlyProvider>
    ),
  },
  {
    path: "family-members",
    active: true,
    redirectOnHold: true,
    Component: <FamilyMembersPage />,
  },
  {
    path: "family-tree",
    active: true,
    redirectOnHold: true,
    Component: <FamilyTreePage />,
  },
  {
    path: "review-cart",
    active: true,
    redirectOnHold: true,
    Component: <ReviewCartPage />,
  },
  {
    path: "my-avatar",
    active: true,
    redirectOnHold: true,
    Component: <MyAvatarPage />,
  },
  {
    path: "configure-waiting-room",
    active: true,
    redirectOnHold: true,
    Component: <ConfigureWaitingRoomPage />,
    notRoles: wlsNoMeetService,
  },
  // {
  //   path: "ai-waiting-room",
  //   active: true,
  //   redirectOnHold: true,
  //   Component: <AIWaitingRoomPage />,
  //   notRoles: wlsNoMeetService,
  // },
  {
    path: "ai-tools",
    active: true,
    redirectOnHold: true,
    Component: <AIToolsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "ai-videos",
    active: true,
    redirectOnHold: true,
    Component: <AIVideosPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "buy-credits",
    active: true,
    redirectOnHold: true,
    Component: <BuyCreditsPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "credits-ai",
    active: true,
    redirectOnHold: false,
    Component: <MyCreditsPage />,
  },
  {
    path: "my-crm",
    active: true,
    redirectOnHold: false,
    Component: <CrmPage />,
  },
  {
    path: "crm/plans",
    active: true,
    redirectOnHold: false,
    Component: <PageMaintenance />,
  },
  {
    path: "credits-ai-thank-u",
    active: true,
    redirectOnHold: false,
    Component: <ThankYouPage />,
  },
  {
    path: "credits-ai/checkout",
    active: true,
    redirectOnHold: false,
    Component: <CheckoutEmbedPage />,
  },
  {
    path: "communication-central",
    active: true,
    redirectOnHold: false,
    Component: <CommunicationCentralPage />,
  },
  {
    path: "my-smart-links",
    active: true,
    redirectOnHold: true,
    Component: <MySmartLinksPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "my-smart-links/create",
    active: true,
    redirectOnHold: true,
    Component: <CreateSmartLinkPage />,
    notRoles: wlsNoMeetService,
  },
  {
    path: "my-smart-links/requests",
    active: true,
    redirectOnHold: true,
    notRoles: wlsNoMeetService,
    Component: (
      <SmartLinksAccessGuard>
        <CohostRequestsPage />
      </SmartLinksAccessGuard>
    ),
  },
  {
    path: "notifications",
    active: true,
    redirectOnHold: true,
    notRoles: wlsNoMeetService,
    Component: (
      <SmartLinksAccessGuard>
        <NotificationsPage />
      </SmartLinksAccessGuard>
    ),
  },
];

export const noNavbarRoutes = ["/on-hold"];
